import React, { useState, useEffect } from 'react';
import { Typography, Modal, message, Spin } from 'antd';
import { PlayCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { galleryAPI } from '../utils/api';
import './Gallerystyles.css';

const { Title, Paragraph, Text } = Typography;

interface GalleryItem {
  _id: string;
  type: 'image' | 'video';
  url: string;
  thumbnail?: string;
  title: string;
  description: string;
  location: string;
}

const PureMasonryGallery: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    try {
      setLoading(true);
      const response = await galleryAPI.getAll();
      setGalleryItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching gallery items:', error);
      message.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item: GalleryItem) => {
    setSelectedItem(item);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedItem(null);
  };

  if (loading) {
    return (
      <section className="pure-masonry-gallery" id="gallery">
        <div className="container">
          <div className="section-header">
            <Title level={2}>Trip Gallery</Title>
            <Paragraph>
              Explore stunning moments from our tours across Sri Lanka's most beautiful destinations
            </Paragraph>
          </div>
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
          </div>
        </div>
      </section>
    );
  }

  if (!galleryItems || galleryItems.length === 0) {
    return (
      <section className="pure-masonry-gallery" id="gallery">
        <div className="container">
          <div className="section-header">
            <Title level={2}>Trip Gallery</Title>
            <Paragraph>
              Explore stunning moments from our tours across Sri Lanka's most beautiful destinations
            </Paragraph>
          </div>
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Paragraph>No gallery items available at the moment.</Paragraph>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pure-masonry-gallery" id="gallery">
      <div className="container">
        <div className="section-header">
          <Title level={2}>Trip Gallery</Title>
          <Paragraph>
            Explore stunning moments from our tours across Sri Lanka's most beautiful destinations
          </Paragraph>
        </div>

        <div className="masonry-grid">
          {galleryItems.map((item, index) => (
            <div
              key={item._id}
              className="masonry-item"
              onClick={() => handleItemClick(item)}
            >
              {/* Image or Video Thumbnail */}
              <div className="media-wrapper">
                <img
                  src={item.type === 'video' ? item.thumbnail : item.url}
                  alt={item.title}
                  className="media-image"
                  loading="lazy"
                />

                {/* Video Play Button */}
                {item.type === 'video' && (
                  <div className="video-play-overlay">
                    <PlayCircleOutlined className="play-icon" />
                  </div>
                )}

                {/* Hover Overlay with Description */}
                <div className="hover-overlay">
                  <div className="overlay-content">
                    <div className="location-badge">
                      <EnvironmentOutlined /> {item.location}
                    </div>
                    <div className="item-info">
                      <Title level={5} className="item-title">
                        {item.title}
                      </Title>
                      <Text className="item-description">{item.description}</Text>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal for Full View */}
      <Modal
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width="80%"
        centered
        className="gallery-modal"
      >
        {selectedItem && (
          <div className="modal-content">
            {selectedItem.type === 'image' ? (
              <img
                src={selectedItem.url}
                alt={selectedItem.title}
                className="modal-media"
              />
            ) : selectedItem.url.includes('youtube.com') || selectedItem.url.includes('youtu.be') ? (
              // YouTube embed
              <iframe
                src={selectedItem.url}
                className="modal-media"
                style={{ width: '100%', height: '70vh', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={selectedItem.title}
              />
            ) : (
              <video
                src={selectedItem.url}
                controls
                autoPlay
                className="modal-media"
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        )}
      </Modal>
    </section>
  );
};

export default PureMasonryGallery;